import { useEffect, useState } from 'react'
import { ArrowUp, ArrowDown, GripVertical } from 'lucide-react'
import { sectionTitle, cardTitle, fieldLabel, inputBase } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useAenderung, useFeld, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import { monthlyReportApi } from '../api/monthly-report.api'
import type { MonthlyReportCatalogColumn } from '../api/monthly-report.api'
import type {
  MonthlyReportConfig,
  MonthlyReportHoursFormat,
  MonthlyReportTimeSource,
} from '../types/time-tracking.types'

/**
 * AP-20: ADMIN-Seite zur tenant-weiten Definition des Monatsberichts.
 *
 * Der Bericht in der PWA ist eine FIXE Sperre: Mitarbeiter sehen exakt die hier
 * definierten Spalten in dieser Reihenfolge — keine eigene Personalisierung.
 *
 * **AP-26 Phase B: Der Spaltenkatalog kommt vom Server.** Bis hierher stand er
 * zweimal — einmal als `FIXED_COLUMNS` im Backend, einmal als Konstante auf
 * dieser Seite, gekoppelt nur durch einen Kommentar. AP-24 hat das bewusst
 * stehen lassen, mit der Vorhersage, es werde beim nächsten Spaltenzuwachs
 * zuschlagen; AP-26 verdoppelt den Katalog auf 18 Einträge. Seitdem liefert
 * `GET /monthly-report/column-catalog` die Liste — fixe Spalten und die aus den
 * Zusatzfeldern abgeleiteten Diäten-Spalten in einem Zug. Neue Spalten
 * erscheinen hier nach einem Backend-Deploy von allein.
 *
 * **AP-36 Phase C: ohne Knopf.** Stundenquelle und -format speichern sofort, der
 * Bestätigungstext beim Verlassen — je als ein Schlüssel von `monthlyReportConfig`
 * (der Server führt einen Schlüssel tief zusammen). Häkchen und Reihenfolge der
 * Spalten sind Operationen auf dem aktuellen Stand der Spaltenliste (8.3 a).
 */

interface EditableColumn {
  key: string
  label: string
  enabled: boolean
  isLv: boolean
}

/**
 * Vereint gespeicherte Config mit dem aktuellen Katalog:
 *  – gespeicherte Reihenfolge/enabled bleiben erhalten
 *  – neu hinzugekommene Spalten werden hinten als deaktiviert angehängt
 *  – Spalten, deren Feld nicht mehr existiert, fallen weg
 */
function mergeConfig(
  saved: MonthlyReportConfig | null,
  catalog: EditableColumn[],
): EditableColumn[] {
  if (!saved || !Array.isArray(saved.columns) || saved.columns.length === 0) {
    return catalog
  }
  const byKey = new Map(catalog.map((c) => [c.key, c]))
  const ordered = [...saved.columns].sort((a, b) => a.order - b.order)
  const result: EditableColumn[] = []
  const seen = new Set<string>()
  for (const c of ordered) {
    const cat = byKey.get(c.key)
    if (!cat) continue // Feld existiert nicht mehr
    result.push({ ...cat, enabled: c.enabled !== false })
    seen.add(c.key)
  }
  // Neue Spalten, die noch nicht in der Config stehen → hinten, deaktiviert.
  for (const cat of catalog) {
    if (!seen.has(cat.key)) result.push({ ...cat, enabled: false })
  }
  return result
}

/** Katalogeintrag des Servers → bearbeitbare Zeile dieser Seite. */
const toEditable = (c: MonthlyReportCatalogColumn): EditableColumn => ({
  key: c.key,
  label: c.label,
  enabled: c.defaultEnabled,
  isLv: c.isLv,
})

/** Die bearbeitbare Spaltenliste aus einem Stand der Konfiguration. */
const spaltenAus = (daten: Datensatz | undefined, katalog: EditableColumn[]) =>
  mergeConfig((daten?.monthlyReportConfig as MonthlyReportConfig | null | undefined) ?? null, katalog)

const alsGespeichert = (cols: EditableColumn[]) => cols.map((c, i) => ({ key: c.key, enabled: c.enabled, order: i }))

export default function MonthlyReportConfigPage() {
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  const [katalog, setKatalog] = useState<EditableColumn[] | null>(null)
  const [vorgabeText, setVorgabeText] = useState('')
  const [katalogFehler, setKatalogFehler] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    monthlyReportApi
      .columnCatalog()
      .then(({ data }) => {
        if (!active) return
        setKatalog(data.columns.map(toEditable))
        // Der Server normalisiert: ohne eigenen Text steht dort seine Vorgabe.
        setVorgabeText(data.config.confirmationText)
      })
      .catch(() => active && setKatalogFehler('Konfiguration konnte nicht geladen werden.'))
    return () => {
      active = false
    }
  }, [])

  if (fehler || katalogFehler) return <div className="p-6 text-destructive">{fehler ?? katalogFehler}</div>
  if (!geladen || !katalog) return <div className="p-6 text-muted-foreground">Lädt…</div>
  return <Monatsbericht stelle={stelle as Speicherstelle<unknown>} katalog={katalog} vorgabeText={vorgabeText} />
}

function Monatsbericht({ stelle, katalog, vorgabeText }: { stelle: Speicherstelle<unknown>; katalog: EditableColumn[]; vorgabeText: string }) {
  const daten = useStellenDaten(stelle)
  const quelle = useFeld<MonthlyReportTimeSource>(stelle, 'monthlyReportConfig.timeSource', {
    art: 'auswahl',
    label: 'Herkunft der Stunden',
    lies: (x) => x as MonthlyReportTimeSource,
    schreib: (w) => (w === 'MANUAL' ? 'MANUAL' : 'TIME_ENTRIES'),
  })
  const format = useFeld<MonthlyReportHoursFormat>(stelle, 'monthlyReportConfig.hoursFormat', {
    art: 'auswahl',
    label: 'Stundenformat',
    lies: (x) => x as MonthlyReportHoursFormat,
    schreib: (w) => (w === 'hhmm' ? 'hhmm' : 'decimal'),
  })
  // Leer ist erlaubt — dann gilt die Vorgabe des Servers (`parseConfig`).
  const bestaetigung = useFeld<string | null>(stelle, 'monthlyReportConfig.confirmationText', { label: 'Bestätigungstext' })
  const spaltenAenderung = useAenderung(stelle, 'monthlyReportConfig.columns')

  const columns = spaltenAus(daten, katalog)
  const enabledCount = columns.filter((c) => c.enabled).length
  const timeSource = quelle.wert === 'MANUAL' ? 'MANUAL' : 'TIME_ENTRIES'
  const hoursFormat = format.wert === 'hhmm' ? 'hhmm' : 'decimal'

  // Am Schlüssel, nicht am Index: Hat inzwischen jemand umsortiert, trifft es trotzdem die richtige Spalte.
  const aendereSpalten = (umbau: (cols: EditableColumn[]) => EditableColumn[]) =>
    void spaltenAenderung.sende({
      felder: ['monthlyReportConfig.columns'],
      labels: { 'monthlyReportConfig.columns': 'Spalten' },
      operation: (aktuell) => ({ monthlyReportConfig: { columns: alsGespeichert(umbau(spaltenAus(aktuell, katalog))) } }),
    })
  const toggle = (key: string, an: boolean) => aendereSpalten((cols) => cols.map((c) => (c.key === key ? { ...c, enabled: an } : c)))
  const move = (key: string, dir: -1 | 1) =>
    aendereSpalten((cols) => {
      const index = cols.findIndex((c) => c.key === key)
      const target = index + dir
      if (index < 0 || target < 0 || target >= cols.length) return cols
      const next = [...cols]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })


  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <div>
        <h1 className={sectionTitle}>Monatsbericht</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Legt fest, welche Inhalte der Monatsbericht enthält, den jeder Mitarbeiter in der App
          erstellt und rechtsverbindlich abgibt. Diese Definition gilt für alle Mitarbeiter —
          eine individuelle Anpassung ist bewusst nicht möglich.
        </p>
      </div>

      {/* AP-24: Herkunft der Stunden */}
      <section className="space-y-2">
        <h2 className={`${cardTitle} flex items-center gap-2`}>
          Herkunft der Stunden <FeldStatusZeichen status={quelle} />
        </h2>
        <p className="text-sm text-muted-foreground">
          Standard für alle Mitarbeiter. Einzelne Mitarbeiter lassen sich davon abweichend
          einstellen — unter Einrichtung → Stundensätze je Mitarbeiter, Spalte „Stunden Monatsbericht".
        </p>
        <div className="flex flex-wrap gap-2">
          {([
            ['TIME_ENTRIES', 'Aus der Zeiterfassung'],
            ['MANUAL', 'Händisch erfasst'],
          ] as [MonthlyReportTimeSource, string][]).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => quelle.setze(value)}
              className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                timeSource === value
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {timeSource === 'TIME_ENTRIES'
            ? 'Die Stunden entstehen aus den Stempelungen. Der Bericht ist nicht bearbeitbar.'
            : 'Mitarbeiter tragen ihre Tagesstunden selbst ein. Wer trotzdem stempelt, bekommt die gestempelten Werte als Vorschlag und kann sie korrigieren.'}
        </p>
        <FeldStatus status={quelle} zeichen={false} />
      </section>

      {/* Stundenformat */}
      <section className="space-y-2">
        <h2 className={`${cardTitle} flex items-center gap-2`}>
          Stundenformat <FeldStatusZeichen status={format} />
        </h2>
        <p className="text-sm text-muted-foreground">
          Gilt tenant-weit für die Anzeige von Stunden auf allen Seiten und in allen Exporten
          (Zeiterfassung, Lohnverrechnung, Monatsbericht). Dezimal zeigt max. 2 Kommastellen ohne
          überflüssige Nullen (8 · 8,5 · 8,25).
        </p>
        <div className="flex gap-2">
          {(['decimal', 'hhmm'] as MonthlyReportHoursFormat[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => format.setze(f)}
              className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                hoursFormat === f
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground'
              }`}
            >
              {f === 'decimal' ? 'Dezimal (8,5)' : 'Stunden:Minuten (8:30)'}
            </button>
          ))}
        </div>
        <FeldStatus status={format} zeichen={false} />
      </section>

      {/* Spalten */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className={`${cardTitle} flex items-center gap-2`}>
            Spalten &amp; Reihenfolge <FeldStatusZeichen status={spaltenAenderung.status} />
          </h2>
          <span className="text-xs text-muted-foreground">{enabledCount} aktiv</span>
        </div>
        <ul className="divide-y divide-border rounded-md border border-border">
          {columns.map((c, i) => (
            <li key={c.key} className="flex items-center gap-3 px-3 py-2">
              <GripVertical size={16} className="shrink-0 text-muted-foreground/50" />
              <input
                type="checkbox"
                checked={c.enabled}
                onChange={(e) => toggle(c.key, e.target.checked)}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                id={`col-${c.key}`}
              />
              <label htmlFor={`col-${c.key}`} className="flex-1 cursor-pointer text-sm">
                {c.label}
                {c.isLv && (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Diäten
                  </span>
                )}
              </label>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => move(c.key, -1)}
                  disabled={i === 0}
                  className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30"
                  aria-label="Nach oben"
                >
                  <ArrowUp size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => move(c.key, 1)}
                  disabled={i === columns.length - 1}
                  className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30"
                  aria-label="Nach unten"
                >
                  <ArrowDown size={15} />
                </button>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          Diäten-Spalten stammen aus den Lohnverrechnungs-Zusatzfeldern (Typ B). Neue Felder
          erscheinen hier automatisch und müssen einmal aktiviert werden. Dasselbe gilt für
          Uhrzeiten (Kommt/Geht) und die Montage- und Reisefelder: Sie stehen bereit, sind
          aber abgeschaltet, bis du sie hier aktivierst.
        </p>
        <FeldStatus status={spaltenAenderung.status} zeichen={false} />
      </section>

      {/* Bestätigungstext */}
      <section className="space-y-2">
        <h2 className={cardTitle}>Bestätigungstext bei der Abgabe</h2>
        <label className={`${fieldLabel} flex items-center gap-1.5`} htmlFor="confirmText">
          Diesem Text stimmt der Mitarbeiter bei der verbindlichen Abgabe zu <FeldStatusZeichen status={bestaetigung} />
        </label>
        <textarea id="confirmText" rows={3} placeholder={vorgabeText} className={`${inputBase} resize-none`} {...bestaetigung.props} />
        <FeldStatus status={bestaetigung} zeichen={false} />
        <p className="text-xs text-muted-foreground">Leer lassen, um den vorgegebenen Text zu verwenden.</p>
      </section>
    </div>
  )
}
